
import { Button, Card, CardContent, FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import _ from 'lodash';
import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLoadingState } from '../../components/admin/Loading';
import { PlayButton } from '../../components/admin/PlayButton';
import { Metadata } from '../../components/admin/QuestionComponents';
import { QuestionFiltersProvider } from '../../components/admin/QuestionFilterContext';
import { QuestionSelector } from '../../components/admin/QuestionSelector';
import { ShareQuestionSetButton } from '../../components/admin/ShareUrlButton';
import { QuestionSetTagEntry } from '../../components/admin/Tags';
import { WideAdminLayout } from '../../components/layout/AdminLayout';
import { useAuthenticatedUser } from '../../contexts/AuthContext';
import { withErrorAlerts } from '../../data/Errors';
import { QuestionOperations, QuestionSetOperations } from '../../data/Firestore';
import { QuestionConfig, QuestionId, QuestionSetConfig, QuestionSetId } from '../../types/GameplayTypes';
import { muiStyleSheet } from '../../util/Styles';

export const EditQuestionSetScreen: FunctionComponent = () =>
	<QuestionFiltersProvider>
		<EditQuestionSetScreenContent />
	</QuestionFiltersProvider>;

type LocationState = {
	template?: QuestionSetConfig;
};

const EditQuestionSetScreenContent: FunctionComponent = () => {
	const { id } = useParams<{ id: QuestionSetId | 'new'; }>();
	const location = useLocation() as { state?: LocationState; };
	const navigate = useNavigate();
	const { user } = useAuthenticatedUser();
	const isNew = id === 'new';

	const [questions, setQuestions] = useState<QuestionConfig[]>([]);
	const [questionSet, setQuestionSet] = useState<QuestionSetConfig | undefined>();

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [tags, setTags] = useState<string[]>([]);
	const [isActive, setIsActive] = useState(true);
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
	const [orderedQuestionIds, setOrderedQuestionIds] = useState<QuestionId[]>([]);

	const { load: loadData, loading, error } = useLoadingState({
		message: isNew ? 'Preparing...' : 'Loading Question Set...',
		failureMessage: 'Failed to load Question Set'
	}, async function () {
		const allQuestions = await QuestionOperations.getAll();
		setQuestions(allQuestions);

		const loadedQuestionSet = (!isNew && id)
			? await QuestionSetOperations.get(id)
			: location.state?.template;

		setQuestionSet(loadedQuestionSet);
		setName(loadedQuestionSet?.name ?? '');
		setDescription(loadedQuestionSet?.description ?? '');
		setTags([...loadedQuestionSet?.tags ?? []]);
		setIsActive(loadedQuestionSet?.isActive ?? true);
		const questionIds = loadedQuestionSet?.questionIds ?? [];
		setSelectionModel({ type: 'include', ids: new Set(questionIds) });
		setOrderedQuestionIds(questionIds);
	});

	useEffect(function initialLoad() {
		void loadData();
	}, [loadData]);

	function selectionModelChanged(newModelOrUpdater: GridRowSelectionModel | ((prev: GridRowSelectionModel) => GridRowSelectionModel)) {
		const newModel = typeof newModelOrUpdater === 'function'
			? newModelOrUpdater(selectionModel)
			: newModelOrUpdater;

		setSelectionModel(newModel);

		// Add newly selected questions to the end of the order
		const currentSet = new Set(orderedQuestionIds);
		const newIds = Array.from(newModel.ids) as QuestionId[];
		const addedIds = newIds.filter(id => !currentSet.has(id));

		// Remove deselected questions from the order
		const remainingOrdered = orderedQuestionIds.filter(id => newModel.ids.has(id));

		setOrderedQuestionIds([...remainingOrdered, ...addedIds]);
	}

	function hasUnsavedChanges(): boolean {
		return (name.trim() !== (questionSet?.name ?? ''))
			|| (description.trim() !== (questionSet?.description ?? ''))
			|| (isActive !== (questionSet?.isActive ?? true))
			|| !_.isEqual(orderedQuestionIds, questionSet?.questionIds ?? [])
			|| !tags.isEqualUnordered(questionSet?.tags ?? []);
	}

	async function handleSave() {
		if (!name.trim()) {
			alert('Question Set name is required');
			return;
		}

		if (orderedQuestionIds.empty()) {
			alert('At least one question must be selected');
			return;
		}

		await withErrorAlerts('Failed to save Question Set', async () => {
			const questionSet = {
				name: name.trim(),
				description: description.trim(),
				tags,
				isActive,
				questionIds: orderedQuestionIds,
			};
			if (isNew) {
				await QuestionSetOperations.create({
					...questionSet,
					authorName: user.displayName ?? 'Unknown',
				});
			} else if (id) {
				await QuestionSetOperations.update(id, questionSet);
			}
			closeScreen();
		});
	}

	function cancel() {
		if (!hasUnsavedChanges()
			|| confirm('You have unsaved changes. Are you sure you want to cancel?')) {
			closeScreen();
		}
	}

	function closeScreen() {
		void navigate('/admin/question-sets');
	}

	const title = isNew ? 'Create Question Set' : 'Edit Question Set';
	const unsavedChanges = hasUnsavedChanges();

	return <WideAdminLayout title={title} loading={loading} error={error}
		actions={<>
			{!isNew && questionSet && <>
				<ShareQuestionSetButton questionSet={questionSet} />
				<PlayButton questionSet={questionSet} hasUnsavedChanges={unsavedChanges} />
			</>}
			<Button variant="outlined" onClick={cancel}>Cancel</Button>
			<Button
				variant="contained"
				onClick={handleSave}
				disabled={!unsavedChanges}
			>
				{isNew ? 'Create' : 'Save'}
			</Button>
		</>}>
		<Card>
			<CardContent>
				<Grid container spacing={2}>
					<Grid size={12}>
						<TextField label="Name" fullWidth required
							value={name} onChange={(e) => setName(e.target.value)} />
					</Grid>

					<Grid size={12}>
						<TextField label="Description" fullWidth multiline rows={3}
							value={description} onChange={(e) => setDescription(e.target.value)} />
					</Grid>

					<Grid size={12}>
						<QuestionSetTagEntry tags={tags} setTags={setTags} />
					</Grid>

					<Grid size={4}>
						<FormControlLabel
							control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
							label="Active"
						/>
					</Grid>
					<Grid size={6} offset={2} sx={styles.metadata}>
						{questionSet && <Metadata {...questionSet} />}
					</Grid>
				</Grid>
			</CardContent>
		</Card>

		<QuestionSelector
			questions={questions}
			selectionModel={selectionModel}
			setSelectionModel={selectionModelChanged}
			orderedQuestionIds={orderedQuestionIds}
			onSelectionReorder={setOrderedQuestionIds}
		/>
	</WideAdminLayout>;
};

const styles = muiStyleSheet({
	metadata: {
		textAlign: 'right',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	}
});